import { useEffect, useMemo, useState } from "react";
import { formatDateTime } from "../../utils/datetime";
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { ClipboardList, Download, Eye, Filter, MessageSquare, RefreshCw, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import APICall from "../../APICalls/APICall";

/**
 * FormSubmissions — lists data captured by Flow Builder "API Call" nodes
 * (completed form/flow submissions), scoped to the current tenant.
 * Supports CSV export and a per-submission detail view.
 */
export default function FormSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null); // submission shown in detail modal
  const [flowFilter, setFlowFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [flowOptions, setFlowOptions] = useState([]); // distinct flow_ids seen
  const navigate = useNavigate();

  const fetchSubmissions = async (filters = {}) => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      const fid = filters.flowFilter ?? flowFilter;
      const from = filters.dateFrom ?? dateFrom;
      const to = filters.dateTo ?? dateTo;
      if (fid) params.set("flow_id", fid);
      if (from) params.set("date_from", from);
      if (to) params.set("date_to", to);
      const q = params.toString();
      const res = await APICall.getT(`/conversation/form-submissions${q ? `?${q}` : ""}`);
      const list = Array.isArray(res?.submissions) ? res.submissions : [];
      setSubmissions(list);
      // Accumulate distinct flow_ids so the dropdown stays populated across filters.
      setFlowOptions((prev) => {
        const set = new Set(prev);
        list.forEach((s) => s.flow_id != null && set.add(s.flow_id));
        return [...set].sort((a, b) => a - b);
      });
    } catch (e) {
      setError(e?.message || "Failed to load form submissions.");
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const applyFilters = () => fetchSubmissions();
  const clearFilters = () => {
    setFlowFilter("");
    setDateFrom("");
    setDateTo("");
    fetchSubmissions({ flowFilter: "", dateFrom: "", dateTo: "" });
  };
  const hasFilters = flowFilter || dateFrom || dateTo;

  // Union of all field keys across submissions → stable columns.
  const fieldKeys = useMemo(() => {
    const keys = [];
    for (const s of submissions) {
      for (const k of Object.keys(s.data || {})) {
        if (!keys.includes(k)) keys.push(k);
      }
    }
    return keys;
  }, [submissions]);

  const fmtDate = (iso) => formatDateTime(iso);

  // ── CSV export ─────────────────────────────────────────────────────────
  const csvCell = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const exportCsv = () => {
    if (!submissions.length) return;
    const headers = ["id", "flow_id", ...fieldKeys, "session_key", "created_at"];
    const lines = [headers.map(csvCell).join(",")];
    for (const s of submissions) {
      const row = [
        s.id,
        s.flow_id,
        ...fieldKeys.map((k) => s.data?.[k] ?? ""),
        s.session_key,
        s.created_at,
      ];
      lines.push(row.map(csvCell).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `form-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-3 p-md-4">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          <ClipboardList size={22} className="text-primary" />
          <div>
            <h4 className="mb-0">Form Submissions</h4>
            <small className="text-muted">
              Data collected by flow "API Call" nodes (e.g. quote requests)
            </small>
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-success"
            size="sm"
            onClick={exportCsv}
            disabled={loading || submissions.length === 0}
          >
            <Download size={15} className="me-1" /> Export CSV
          </Button>
          <Button variant="outline-primary" size="sm" onClick={fetchSubmissions} disabled={loading}>
            <RefreshCw size={15} className="me-1" /> Refresh
          </Button>
        </div>
      </div>

      <Card className="shadow-sm border-0 mb-3">
        <Card.Body className="py-3">
          <Row className="g-2 align-items-end">
            <Col xs={12} md={3}>
              <Form.Label className="small text-muted mb-1">
                <Filter size={13} className="me-1" /> Flow
              </Form.Label>
              <Form.Select size="sm" value={flowFilter} onChange={(e) => setFlowFilter(e.target.value)}>
                <option value="">All flows</option>
                {flowOptions.map((id) => (
                  <option key={id} value={id}>Flow #{id}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={3}>
              <Form.Label className="small text-muted mb-1">From</Form.Label>
              <Form.Control size="sm" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </Col>
            <Col xs={6} md={3}>
              <Form.Label className="small text-muted mb-1">To</Form.Label>
              <Form.Control size="sm" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </Col>
            <Col xs={12} md={3} className="d-flex gap-2">
              <Button variant="primary" size="sm" onClick={applyFilters} disabled={loading} className="flex-fill">
                Apply
              </Button>
              {hasFilters && (
                <Button variant="outline-secondary" size="sm" onClick={clearFilters} disabled={loading}>
                  <X size={15} />
                </Button>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm border-0">
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" size="sm" /> <span className="ms-2">Loading…</span>
            </div>
          ) : error ? (
            <div className="text-danger py-4 text-center">{error}</div>
          ) : submissions.length === 0 ? (
            <div className="text-muted py-5 text-center">
              No form submissions yet. Complete a form flow in the chatbot and it will appear here.
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>#</th>
                    <th style={{ width: 90 }}>Flow</th>
                    {fieldKeys.map((k) => (
                      <th key={k} className="text-capitalize">{k.replace(/_/g, " ")}</th>
                    ))}
                    <th>Source</th>
                    <th style={{ width: 170 }}>Submitted</th>
                    <th style={{ width: 70 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((s, i) => (
                    <tr key={s.id} style={{ cursor: "pointer" }} onClick={() => setSelected(s)}>
                      <td className="text-muted">{i + 1}</td>
                      <td><Badge bg="secondary">#{s.flow_id}</Badge></td>
                      {fieldKeys.map((k) => (
                        <td key={k}>{s.data?.[k] ?? <span className="text-muted">—</span>}</td>
                      ))}
                      <td className="small text-muted">{s.session_key}</td>
                      <td className="small">{fmtDate(s.created_at)}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <Button variant="link" size="sm" className="p-0" onClick={() => setSelected(s)}>
                          <Eye size={16} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* ── Detail modal ── */}
      <Modal show={!!selected} onHide={() => setSelected(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h6 mb-0">
            Submission #{selected?.id} <Badge bg="secondary" className="ms-1">Flow #{selected?.flow_id}</Badge>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selected && (
            <>
              <div className="mb-3 small text-muted">
                <div><strong>Session:</strong> {selected.session_key}</div>
                <div><strong>Submitted:</strong> {fmtDate(selected.created_at)}</div>
              </div>
              <Table size="sm" bordered className="mb-3">
                <tbody>
                  {Object.entries(selected.data || {}).map(([k, v]) => (
                    <tr key={k}>
                      <th className="text-capitalize" style={{ width: "40%" }}>{k.replace(/_/g, " ")}</th>
                      <td>{v == null || v === "" ? <span className="text-muted">—</span> : String(v)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <details>
                <summary className="small text-muted" style={{ cursor: "pointer" }}>Raw JSON</summary>
                <pre className="small bg-light p-2 rounded mt-2 mb-0" style={{ maxHeight: 240, overflow: "auto" }}>
                  {JSON.stringify(selected.data, null, 2)}
                </pre>
              </details>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {selected?.session_key && (
            <Button
              variant="outline-primary"
              size="sm"
              className="me-auto"
              onClick={() => navigate(`/Conversations?session=${encodeURIComponent(selected.session_key)}`)}
            >
              <MessageSquare size={15} className="me-1" /> View Conversation
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={() => setSelected(null)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
